<template>
  <b-modal
    :visible="visible"
    :title="isEditing ? 'Edit Note' : 'New Note'"
    size="lg"
    ok-title="Save Note"
    :ok-disabled="saving"
    :cancel-disabled="saving"
    no-close-on-backdrop
    @ok.prevent="handleSubmit"
    @hidden="$emit('update:visible', false)"
  >
    <validation-observer ref="noteRules" tag="form">
      <b-row>
        <b-col md="12">
          <b-form-group label="About" label-for="subject_ids">
            <label class="required-l" for="subject_ids">Who is this note about?</label>
            <validation-provider #default="{ errors }" name="About" rules="required">
              <v-select
                id="subject_ids"
                v-model="form.subject_ids"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="employeeListOptions"
                label="value"
                multiple
                :reduce="(val) => val.id"
                placeholder="Select one or more employees.."
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>
        </b-col>

        <b-col md="12" v-if="isGroupNote">
          <b-form-group label-for="title">
            <label class="required-l" for="title">Title / topic</label>
            <validation-provider #default="{ errors }" name="Title" rules="required">
              <b-form-input
                id="title"
                v-model="form.title"
                placeholder="What's this note about?"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>
        </b-col>

        <b-col md="6">
          <b-form-group label-for="noted_at">
            <label for="noted_at">Date</label>
            <flat-pickr
              id="noted_at"
              v-model="form.noted_at"
              class="form-control"
              :config="{
                enableTime: true,
                dateFormat: 'Y-m-d H:i',
                altFormat: 'M j, Y h:i K',
                altInput: true,
              }"
            />
          </b-form-group>
        </b-col>

        <b-col md="12">
          <b-form-group label-for="content">
            <label class="required-l" for="content">Note</label>
            <validation-provider #default="{ errors }" name="Note" rules="required">
              <QuillInput
                :value.sync="form.content"
                :toolbar="true"
                className="personal-note-editor"
                :mentionList="employeeListOptions"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>
        </b-col>

        <b-col md="12" class="mb-1">
          <b-button
            variant="outline-primary"
            size="sm"
            :disabled="refining || isContentEmpty"
            @click="refineWithAi"
          >
            <b-spinner v-if="refining" small class="mr-50" />
            <feather-icon v-else icon="ZapIcon" class="mr-50" />
            Refine with AI
          </b-button>
          <small v-if="form.ai_refined" class="text-success ml-1">
            <feather-icon icon="CheckIcon" /> Refined -- original preserved as source
          </small>
        </b-col>

        <b-col md="12">
          <b-form-group label-for="tags">
            <label for="tags">Tags</label>
            <div class="d-flex flex-wrap align-items-center note-tags-input">
              <b-badge
                v-for="(tag, index) in form.tags"
                :key="index"
                pill
                variant="light-primary"
                class="mr-50 mb-50"
              >
                {{ tag }}
                <feather-icon
                  icon="XIcon"
                  size="12"
                  class="cursor-pointer ml-25"
                  @click="removeTag(index)"
                />
              </b-badge>
              <b-form-input
                v-model="newTag"
                size="sm"
                style="max-width: 160px"
                placeholder="Add a tag.."
                @keydown.enter.prevent="addTag"
              />
            </div>
          </b-form-group>
        </b-col>
      </b-row>
    </validation-observer>
  </b-modal>
</template>

<script>
import {
  BModal,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BButton,
  BBadge,
  BSpinner,
} from "bootstrap-vue";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { required } from "@validations";
import QuillInput from "@/components/QuillInput.vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import axios from "@axios";

export default {
  name: "PersonalNoteForm",
  components: {
    BModal,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BButton,
    BBadge,
    BSpinner,
    vSelect,
    flatPickr,
    ValidationProvider,
    ValidationObserver,
    QuillInput,
  },
  props: {
    visible: { type: Boolean, default: false },
    note: { type: Object, default: null },
    employeeListOptions: { type: Array, default: () => [] },
    defaultSubjectId: { type: String, default: null },
  },
  data() {
    return {
      required,
      saving: false,
      refining: false,
      newTag: "",
      form: this.blankForm(),
    };
  },
  computed: {
    isEditing() {
      return !!(this.note && this.note.hashid);
    },
    isGroupNote() {
      return this.form.subject_ids.length > 1;
    },
    isContentEmpty() {
      return !this.form.content || !this.form.content.replace(/<[^>]*>/g, "").trim();
    },
  },
  watch: {
    note: {
      immediate: true,
      handler(val) {
        this.form = this.blankForm(val);
      },
    },
    visible(val) {
      if (val) {
        this.form = this.blankForm(this.note);
      }
    },
  },
  methods: {
    blankForm(note) {
      if (note) {
        return {
          subject_ids: (note.subjects || []).map((s) => s.hashid),
          title: note.title || "",
          noted_at: note.noted_at
            ? note.noted_at.slice(0, 16).replace("T", " ")
            : this.nowFormatted(),
          content: note.content || "",
          noted_source: note.noted_source || null,
          tags: note.tags ? [...note.tags] : [],
          ai_refined: !!note.ai_refined,
        };
      }
      return {
        subject_ids: this.defaultSubjectId ? [this.defaultSubjectId] : [],
        title: "",
        noted_at: this.nowFormatted(),
        content: "",
        noted_source: null,
        tags: [],
        ai_refined: false,
      };
    },
    nowFormatted() {
      const d = new Date();
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(
        d.getHours()
      )}:${pad(d.getMinutes())}`;
    },
    addTag() {
      const tag = this.newTag.trim();
      if (tag && !this.form.tags.includes(tag)) {
        this.form.tags.push(tag);
      }
      this.newTag = "";
    },
    removeTag(index) {
      this.form.tags.splice(index, 1);
    },
    refineWithAi() {
      const self = this;
      self.refining = true;
      axios
        .post("/personal-notes/refine", { content: self.form.content })
        .then((res) => {
          self.form.content = res.data.refined_content;
          self.form.tags = res.data.tags || [];
          self.form.noted_source = res.data.noted_source;
          self.form.ai_refined = true;
          self.refining = false;
        })
        .catch(() => {
          self.$toast(
            {
              component: ToastificationContent,
              props: {
                title: "Couldn't refine this note right now.",
                icon: "BellIcon",
                variant: "danger",
              },
            },
            { timeout: 3000, position: "bottom-center" }
          );
          self.refining = false;
        });
    },
    handleSubmit() {
      const self = this;
      this.$refs.noteRules.validate().then((success) => {
        if (!success) return;

        self.saving = true;
        const payload = { ...self.form };

        const request = self.isEditing
          ? axios.put(`/personal-notes/${self.note.hashid}`, payload)
          : axios.post("/personal-notes", payload);

        request
          .then((res) => {
            self.saving = false;
            self.$emit("saved", res.data.data);
            self.$emit("update:visible", false);
            self.$toast(
              {
                component: ToastificationContent,
                props: {
                  title: self.isEditing ? "Note updated" : "Note saved",
                  icon: "CheckIcon",
                  variant: "success",
                },
              },
              { timeout: 2000, position: "bottom-center" }
            );
          })
          .catch((error) => {
            self.saving = false;
            const message =
              (error.response &&
                error.response.data &&
                error.response.data.message) ||
              "Something went wrong saving this note.";
            self.$toast(
              {
                component: ToastificationContent,
                props: { title: message, icon: "BellIcon", variant: "danger" },
              },
              { timeout: 3000, position: "bottom-center" }
            );
          });
      });
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
</style>
